import { useEffect, useState } from 'react';
import { api } from '../api';
import { useRouter } from '../router';
import { UiNode } from '../lib/UiNode';
import type { BoardSummary, User } from '../../shared/types';

interface Intent {
  id: string;
  text: string;
  url: string | null;
  imageR2Key: string | null;
  createdAt: number;
}

/**
 * Share target landing.
 *
 * The Worker stashed the shared payload in a signed cookie during the
 * /share POST. Here we GET /share/intent (which reads and clears it),
 * let the user pick a board, then create the idea via the regular
 * create-idea + attachments routes — same code path as in-app capture.
 */
export function ShareCompose({ user: _user }: { user: User }) {
  const [intent, setIntent] = useState<Intent | null>(null);
  const [boards, setBoards] = useState<BoardSummary[] | null>(null);
  const [busyBoardId, setBusyBoardId] = useState<string | null>(null);
  const { navigate } = useRouter();

  useEffect(() => {
    void Promise.all([api.shareIntent(), api.myBoards()]).then(([i, b]) => {
      setIntent(i.intent);
      setBoards(b.boards);
    });
  }, []);

  const open = boards?.filter((b) => b.phase !== 'archived' && b.phase !== 'shipped') ?? null;

  const sendTo = async (boardId: string) => {
    if (!intent || busyBoardId) return;
    setBusyBoardId(boardId);
    try {
      const baseText = intent.text.trim() || (intent.url ? intent.url : 'Shared image');
      const r = await api.createIdea(boardId, { text: baseText.slice(0, 280) });
      if (intent.url) {
        try {
          await api.addLink(boardId, r.id, intent.url);
        } catch {
          /* keep going */
        }
      }
      // imageR2Key promotion: v1 leaves the temp object orphaned and the
      // user re-attaches an image directly. Promoting share-temp → board
      // media is a v1.1 task; the user can drag the image into the new
      // idea on the board.
      navigate(`/boards/${boardId}`);
    } finally {
      setBusyBoardId(null);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <h1 className="text-2xl">Share to SynFolio</h1>
      {intent ? (
        <SharePreview intent={intent} />
      ) : (
        <p className="text-sm text-cream-3">No shared content to attach. Pick a board to open.</p>
      )}
      <p className="text-sm text-cream-3">Pick a board.</p>
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {(open ?? []).map((b) => (
          <li key={b.id}>
            <UiNode
              as="button"
              name="boards-board-card"
              entity="board"
              entityId={b.id}
              action="open"
              state={b.phase}
              type="button"
              className="panel w-full p-4 text-left transition hover:border-cream-3 disabled:opacity-50"
              disabled={busyBoardId !== null}
              onClick={() => sendTo(b.id)}
            >
              <div className="text-base font-medium">{b.name}</div>
              <div className="mt-0.5 text-xs text-cream-3">
                {busyBoardId === b.id ? 'Adding…' : `${b.ideaCount} ideas`}
              </div>
            </UiNode>
          </li>
        ))}
      </ul>
      {open && open.length === 0 && (
        <p className="text-sm text-cream-3">No active boards. Create one first.</p>
      )}
    </div>
  );
}

function SharePreview({ intent }: { intent: Intent }) {
  return (
    <div className="panel p-4">
      {intent.text && (
        <p className="break-words text-[15px] leading-snug">{intent.text}</p>
      )}
      {intent.url && (
        <p className="mt-1 truncate text-xs text-cream-3">{intent.url}</p>
      )}
      {intent.imageR2Key && (
        <p className="mt-2 text-xs text-cream-3">An image was shared and is pending attach.</p>
      )}
    </div>
  );
}
